// src/App.js
import {useEffect, useRef, useState} from "react";
import "./vestaboard.js"; // registers <vestaboard-board>

export default function App() {
    const boardRef = useRef(null);
    const [msg, setMsg] = useState("HELLO • WORLD 2025");
    const videoRef = useRef(null);
    const bufferRef = useRef(null);
    const rafRef = useRef(null);
    const streamRef = useRef(null);
    const runningRef = useRef(false);
    const settingsRef = useRef({});

    const [camOn, setCamOn] = useState(false);
    const [invert, setInvert] = useState(true);
    const [bw, setBw] = useState(false);
    const [threshold, setThreshold] = useState(128);
    const [dense, setDense] = useState(true); // use letters/numbers & denser ramp

    useEffect(() => {
        const board = boardRef.current;
        if (!board) return;

        // Initial message
        board.setMessage(msg, {stagger: 0.002});

    }, []);

    useEffect(() => {
      settingsRef.current = {
        invert,
        bw,
        threshold,
        dense,
      };
    }, [invert, bw, threshold, dense]);

    const startCamera = async () => {
      if (camOn) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        streamRef.current = stream;

        if (!videoRef.current) {
          console.warn('Video element ref is not attached yet.');
        } else {
          // Ensure correct attributes on the visible video element
          videoRef.current.playsInline = true;
          videoRef.current.muted = true;
          videoRef.current.autoplay = true;
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch {}
        }

        if (!bufferRef.current) bufferRef.current = document.createElement("canvas");

        const board = boardRef.current;
        const rows = board?.rows || 7;
        const cols = board?.cols || 28;
        bufferRef.current.width = cols;
        bufferRef.current.height = rows;

        if (board) { board._playing = false; if (typeof board.stop === 'function') board.stop(); }
        runningRef.current = true;
        setCamOn(true);

        const startLoop = () => { if (runningRef.current) camLoop(); };
        if (videoRef.current.readyState >= 1 && videoRef.current.videoWidth) {
          startLoop();
        } else {
          videoRef.current.addEventListener('loadedmetadata', () => { startLoop(); }, { once: true });
        }
      } catch (err) {
        console.error("Camera error", err);
        alert("Could not access camera: " + err.message);
      }
    };

    const stopCamera = () => {
      runningRef.current = false;
      setCamOn(false);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };


    const camLoop = () => {
      if (!runningRef.current) return;
      const board = boardRef.current;
      const video = videoRef.current;
      const buf = bufferRef.current;

      if (!board || !video || !buf) {
        rafRef.current = requestAnimationFrame(camLoop);
        return;
      }

      // If metadata isn't ready yet, try again next frame
      if (!video.videoWidth || !video.videoHeight) {
        rafRef.current = requestAnimationFrame(camLoop);
        return;
      }

      const {
        invert: INV,
        bw: BW,
        threshold: THR,
        dense: DENSE,
      } = settingsRef.current;

      const ctx = buf.getContext("2d", { willReadFrequently: true });
      const rows = board.rows, cols = board.cols;
      ctx.clearRect(0, 0, cols, rows);
      ctx.imageSmoothingEnabled = false;
      try {
        ctx.drawImage(video, 0, 0, cols, rows);
      } catch {}

      const img = ctx.getImageData(0, 0, cols, rows).data;

      // Build grayscale luminance buffer (0..255)
      const N = rows * cols;
      const gray = new Float32Array(N);
      for (let i = 0, p = 0; i < N; i++, p += 4) {
        const R = img[p], G = img[p+1], B = img[p+2];
        let y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
        if (INV) y = 255 - y;
        gray[i] = y;
      }

      let chars = new Array(rows);

      // Brightness-based ASCII ramp
      const BASIC_RAMP = [" ", ".", ":", ";", "+", "*", "#", "@"]; // light -> dark
      const DENSE_RAMP = [" ", ".", ",", ":", ";", "I", "1", "L", "T", "E", "S", "A", "O", "0", "8", "%", "#", "@"]; // letters & numbers
      const ramp = BW ? [" ", "#"] : (DENSE ? DENSE_RAMP : BASIC_RAMP);
      const levels = ramp.length - 1;
      for (let r = 0; r < rows; r++) {
        let line = '';
        for (let c = 0; c < cols; c++) {
          const Y = gray[r * cols + c];
          let ch;
          if (BW) ch = Y >= THR ? ' ' : '#';
          else {
            const t = Math.max(0, Math.min(levels, Math.round((1 - (Y / 255)) * levels)));
            ch = ramp[t];
          }
          line += ch;
        }
        chars[r] = line;
      }

      if (!chars.length || !chars[0]) {
        console.warn('No chars produced from camera frame');
      } else {
        if (!camLoop._loggedOnce) {
          console.log('ASCII first row:', chars[0]);
          camLoop._loggedOnce = true;
        }
      }

      // Fallback quick test: if we haven't shown a frame yet, push a checkerboard once
      if (!camLoop._pushedTest && board && typeof board.setFromMatrix === 'function') {
        const test = Array.from({ length: rows }, (_, r) =>
          Array.from({ length: cols }, (_, c) => ((r + c) % 2 ? '#' : ' ')).join('')
        );
        board.setFromMatrix(test, { instant: true });
        camLoop._pushedTest = true;
      }

      if (typeof board.setFromMatrix !== 'function') {
        console.warn('vestaboard element missing setFromMatrix; check vestaboard.js import/registration');
      } else {
        const out = chars.map(row => row.split('').reverse().join(''));
        board.setFromMatrix(out, { instant: true });
      }

      rafRef.current = requestAnimationFrame(camLoop);
    };

// Cleanup on unmount
    useEffect(() => () => {
        stopCamera();
    }, []);
    return (
        <div style={{padding: 20, background: "#0b0d10", minHeight: "100vh"}}>
            <h1 style={{color: "#ffffff", fontWeight: 600, marginBottom: 16}}>
                Virtual Vestaboard
            </h1>

            <div style={{marginBottom: 12, display: "flex", gap: 8}}>
                <input
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Type a message…"
                    style={{
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #1f2937",
                        background: "#0e1217",
                        color: "#fff",
                        minWidth: 260
                    }}
                />
                <button
                    onClick={() => {
                        if (boardRef.current) {
                            boardRef.current._playing = false;
                            boardRef.current.setMessage(msg, {stagger: 0.002});
                        }
                    }}
                >
                    Set Message
                </button>
                <button
                    onClick={() =>
                        boardRef.current?.setMessage(msg, {
                            wave: true,
                            waveAmp: 0.18,
                            waveFx: 0.6,
                            waveFy: 0.35,
                        })
                    }
                >
                    Wave Reveal
                </button>
                <button onClick={() => boardRef.current?.randomize()}>
                    Randomize
                </button>
                <button onClick={startCamera} disabled={camOn}>Start Camera</button>
                <button onClick={stopCamera} disabled={!camOn}>Stop Camera</button>
                <label style={{color: "#fff", marginLeft: 8}}>
                    <input type="checkbox" checked={bw} onChange={(e) => setBw(e.target.checked)}/> B/W
                </label>
                <label style={{color: "#fff", marginLeft: 8}}>
                    <input type="checkbox" checked={invert} onChange={(e) => setInvert(e.target.checked)}/> Invert
                </label>
                {!bw && (
                  <label style={{ color: '#fff', marginLeft: 8 }}>
                    <input type="checkbox" checked={dense} onChange={(e)=>setDense(e.target.checked)} /> Dense ramp
                  </label>
                )}
                {bw && (
                    <label style={{color: "#fff", marginLeft: 8}}>
                        Threshold
                        <input type="range" min="0" max="255" value={threshold}
                               onChange={(e) => setThreshold(parseInt(e.target.value, 10))}/>
                    </label>
                )}
                <button onClick={() => {
                  const board = boardRef.current;
                  if (!board || typeof board.setFromMatrix !== 'function') return;
                  const rows = board.rows, cols = board.cols;
                  const test = Array.from({ length: rows }, (_, r) =>
                    Array.from({ length: cols }, (_, c) => ((r + c) % 2 ? '#' : ' ')).join('')
                  );
                  board.setFromMatrix(test, { instant: true });
                }}>
                  Test Pattern
                </button>
            </div>

            <vestaboard-board
                ref={boardRef}
                rows="7"
                cols="28"
                cell="24"
                gap="4"
                speed="1"
                theme="dark"
                style={{width: "100%", height: "480px", display: "block"}}
            />

            <div style={{ marginBottom: 12 }}>
                <video
                    ref={videoRef}
                    style={{
                        width: '100%',
                        maxWidth: 720,
                        height: 'auto',
                        background: '#000',
                        border: '1px solid #1f2937',
                        borderRadius: 8,
                        display: 'block',
                        margin: '0 auto',
                        transform: 'scaleX(-1)'
                    }}
                    playsInline
                    muted
                    autoPlay
                />
                <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', marginTop: 6 }}>
                    Live camera preview (downsampled to {String( (boardRef.current && boardRef.current.rows) || 7 )}×{String( (boardRef.current && boardRef.current.cols) || 28 )} for the ASCII board)
                </div>
            </div>
        </div>
    );
}